import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Send, ShieldAlert } from "lucide-react";
import { listMessages, postMessage } from "../../services/archMarketplace";

/**
 * PII-safe Q&A thread for a marketplace project. Contact info (email/phone/URLs/meeting links) is
 * redacted server-side; a redacted message shows a small shield note.
 *
 * Props: projectId, myEntity ("ARCHITECT" | "FREELANCER"), disabled.
 */
function ChatPanel({ projectId, myEntity, disabled = false }) {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = async () => {
    try {
      const data = await listMessages(projectId);
      setMessages(Array.isArray(data) ? data : []);
    } catch (e) {
      // silent — chat is secondary
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      const msg = await postMessage(projectId, body.trim());
      setMessages((m) => [...m, msg]);
      setBody("");
      if (msg?.wasRedacted) {
        toast.info("Contact details were removed from your message.");
      }
    } catch (e) {
      toast.error(e?.detail || e?.response?.data?.detail || "Could not send message.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-96 border border-gray-200 rounded-lg bg-white">
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading ? (
          <p className="text-sm text-gray-400">Loading messages…</p>
        ) : messages.length === 0 ? (
          <p className="text-sm text-gray-400">No messages yet. Ask a question about the project.</p>
        ) : (
          messages.map((m) => {
            const mine = m.senderEntity === myEntity;
            return (
              <div key={m.messageId} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${
                    mine ? "bg-flamingo-500 text-white" : "bg-gray-100 text-gray-800"
                  }`}
                >
                  <div>{m.body}</div>
                  {m.wasRedacted && (
                    <div className={`mt-1 flex items-center gap-1 text-[11px] ${mine ? "text-flamingo-100" : "text-amber-600"}`}>
                      <ShieldAlert size={12} /> contact info removed
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>
      <div className="border-t border-gray-200 p-3 flex items-center gap-2">
        <input
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
          disabled={disabled || sending}
          placeholder={disabled ? "Chat available once assigned" : "Type a message…"}
          className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-300 disabled:bg-gray-50"
        />
        <button
          type="button"
          onClick={send}
          disabled={disabled || sending || !body.trim()}
          className="inline-flex items-center gap-1 rounded-md bg-flamingo-600 px-3 py-2 text-sm font-medium text-white hover:bg-flamingo-700 disabled:opacity-50"
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );
}

export default ChatPanel;
